import { useEffect, useRef, useState } from "react";
import type { PublicProfessionalProfile } from "../../features/profile/operations";
import { signInWithEmail } from "../../auth/emailAuth";
import { CLAIM_CEILING_MS, claimGuestIdentity } from "../../lib/guestSession";
import {
  canOfferBookProviderSignIn,
  classifyProviderFailure,
  signInWithBookProvider,
  warmBookProviderSignIn,
  type BookProvider,
} from "../../auth/providerSignIn";
import StepSignIn, { type SignInFailure } from "./StepSignIn";
import { accountCreateChunk } from "./lazySteps";
import { useSignOutNotice } from "../../../../../shared/hooks/useSignOutNotice";

interface Props {
  slug: string;
  profile: PublicProfessionalProfile;
  /** Releases the flow's hold under the identity that owns it. */
  releaseHold: () => Promise<void>;
  bookingSafe: boolean;
  /** Book's `guardSearch`: the query, without a time hidden on Back (WS9-1). */
  search: string;
}

/**
 * The hold is released only when the UID is about to change.
 *
 * A guest who LINKS Apple or Google keeps its UID, so its hold stays theirs and
 * nothing is released. Email sign-in, and a provider account that already
 * belongs to another user, replace the UID: the release is awaited first, while
 * the guest's token still authorizes it. The new UID acquires a NEW hold on
 * Time; the old id is never committed under another identity.
 */
function BookSignIn({ slug, profile, releaseHold, bookingSafe, search }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [failure, setFailure] = useState<SignInFailure | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingProvider, setPendingProvider] = useState<BookProvider | null>(null);
  const [providerReady, setProviderReady] = useState(false);
  // An unconfigured host or provider cannot succeed on retry here.
  const [providersWithdrawn, setProvidersWithdrawn] = useState(false);
  // One identity change at a time, decided synchronously: two taps inside one
  // frame both see React's `busy` as false.
  const inFlight = useRef(false);
  // Why the client is signed out, when they were just routed here after it.
  const signOutNotice = useSignOutNotice();

  // Account creation is reachable from here ("Create account", and a sign-in
  // that lands in account setup): its chunk starts now (rc.12 (a)).
  useEffect(() => {
    accountCreateChunk.prefetch();
  }, []);

  // The warm waits for this screen's first paint (or load) before it touches
  // apis.google.com, and the cleanup cancels it: see warmBookProviderSignIn.
  useEffect(() => {
    if (!bookingSafe || !canOfferBookProviderSignIn()) return;
    return warmBookProviderSignIn((ready) => {
      if (ready) setProviderReady(true);
      else setFailure("provider-unavailable");
    });
  }, [bookingSafe]);

  const busy = submitting || pendingProvider !== null;
  const providersOffered = bookingSafe && providerReady && !providersWithdrawn &&
    canOfferBookProviderSignIn();

  function submitEmail(): void {
    if (!bookingSafe || inFlight.current) return;
    const address = email.trim();
    if (!address || !password) return;
    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    void releaseHold()
      .then(() => signInWithEmail(address, password))
      .catch(() => setFailure("credentials"))
      .finally(() => {
        inFlight.current = false;
        setSubmitting(false);
      });
  }

  function submitProvider(provider: BookProvider): void {
    if (!providersOffered || inFlight.current) return;
    inFlight.current = true;
    setPendingProvider(provider);
    setFailure(null);
    // Claimed while the popup is open: an idle rotation in between would link
    // the provider account to a UID that is no longer current. Bounded (W1):
    // an abandoned popup must not hold the identity while the next person
    // books under it; a link that lands after the bound is re-checked and
    // undone (auth/providerSignIn.ts).
    const unclaim = claimGuestIdentity(Number.POSITIVE_INFINITY, CLAIM_CEILING_MS.popup);
    void signInWithBookProvider(provider, releaseHold).catch((error: unknown) => {
      const kind = classifyProviderFailure(error);
      if (kind === "collision") setFailure("provider-collision");
      else if (kind === "blocked") setFailure("provider-blocked");
      else if (kind === "unconfigured") {
        setFailure("provider-unavailable");
        setProvidersWithdrawn(true);
      } else if (kind === "provider") setFailure("provider");
    }).finally(() => {
      unclaim();
      inFlight.current = false;
      setPendingProvider(null);
    });
  }

  return <StepSignIn
    profile={profile} required={profile.requiresClientAccount}
    forgotPasswordHref={bookingSafe && !busy ? `/${slug}/book/reset` : undefined}
    createAccountHref={bookingSafe && !busy ? `/${slug}/book/create` : undefined}
    email={email} password={password} failure={failure}
    submitting={submitting}
    pendingProvider={pendingProvider}
    onChange={(field, value) => {
      if (field === "email") setEmail(value);
      else setPassword(value);
      setFailure(null);
    }}
    onSubmit={submitEmail}
    onProvider={providersOffered ? submitProvider : undefined}
    identitySwitchBlocked={!bookingSafe}
    blockedBackHref={bookingSafe ? undefined : `/${slug}/book/review${search}`}
    signOutNotice={signOutNotice}
  />;
}

export default BookSignIn;
